/**
 * webhooksApi.ts — Wrapper für die fünf Webhook-Endpoint-CRUD-Cloud-Functions
 * (M12d-4b, architecture-m12.md §2.4 + §3.1).
 *
 * Muster: src/api/apiKeysApi.ts (httpsCallable via firebase/functions, gleiche
 * europe-west3-Instanz aus firebaseConfig.ts). Fehlerbehandlung übernimmt
 * zusätzlich das `normalizeCallableError`-Muster aus src/api/advisorInviteApi.ts,
 * damit Aufrufer den Function-Error-Code (insb. `failed-precondition`, wenn das
 * Backend-Flag `WEBHOOKS_ENABLED` aus ist) grob unterscheiden können, ohne interne
 * Details zu leaken.
 *
 * Response-Shapes müssen EXAKT mit
 * functions/src/webhooks/webhookEndpointFunctions.ts übereinstimmen (Quelle der
 * Wahrheit für die Interfaces unten).
 */

import { httpsCallable, HttpsCallableResult } from "firebase/functions";
import { functions } from "../firebaseConfig";
import logger from "../utils/logger";

// ---------------------------------------------------------------------------
// Event-Typen (SSOT für die FE-Auswahl)
// ---------------------------------------------------------------------------

/**
 * Bekannte Webhook-Event-Typen für die Endpoint-Erstellung/-Auswahl.
 * MUSS mit `WEBHOOK_EVENT_TYPES` in `functions/src/webhooks/webhookTypes.ts`
 * übereinstimmen.
 */
export const WEBHOOK_EVENT_TYPES = [
  "invoice.created",
  "invoice.paid",
  "invoice.cancelled",
] as const;

export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

// ---------------------------------------------------------------------------
// Response-Shapes (müssen exakt mit webhookEndpointFunctions.ts übereinstimmen)
// ---------------------------------------------------------------------------

/** Öffentliche Ansicht eines Webhook-Endpoints in der Liste — NIE secretEnc/Klartext. */
export interface WebhookEndpointItem {
  id: string;
  url: string;
  events: WebhookEventType[];
  active: boolean;
  createdAt: string | null;
  lastDeliveryAt: string | null;
  failureCount: number;
}

/** Response von createWebhookEndpoint — Klartext-Secret nur einmalig hier vorhanden. */
export interface CreateWebhookEndpointResponse {
  endpointId: string;
  /** Klartext-Signatursecret — wird NUR HIER EINMAL zurückgegeben. */
  signingSecret: string;
  url: string;
  events: WebhookEventType[];
  active: boolean;
}

/** Response von listWebhookEndpoints. */
interface ListWebhookEndpointsResponse {
  endpoints: WebhookEndpointItem[];
}

/** Response von deleteWebhookEndpoint. */
export interface DeleteWebhookEndpointResponse {
  success: boolean;
}

/** Response von rotateWebhookSecret — Klartext des NEUEN Secrets nur einmalig hier vorhanden. */
export interface RotateWebhookSecretResponse {
  endpointId: string;
  /** Klartext des NEUEN Signatursecrets — wird NUR HIER EINMAL zurückgegeben. */
  signingSecret: string;
  url: string;
  events: WebhookEventType[];
  active: boolean;
}

// ---------------------------------------------------------------------------
// Callable-Function-Referenzen
// ---------------------------------------------------------------------------

const createWebhookEndpointFn = httpsCallable<
  { url: string; events: string[] },
  CreateWebhookEndpointResponse
>(functions, "createWebhookEndpoint");

const listWebhookEndpointsFn = httpsCallable<
  Record<string, never>,
  ListWebhookEndpointsResponse
>(functions, "listWebhookEndpoints");

const updateWebhookEndpointFn = httpsCallable<
  { endpointId: string; url?: string; events?: string[]; active?: boolean },
  WebhookEndpointItem
>(functions, "updateWebhookEndpoint");

const deleteWebhookEndpointFn = httpsCallable<
  { endpointId: string },
  DeleteWebhookEndpointResponse
>(functions, "deleteWebhookEndpoint");

const rotateWebhookSecretFn = httpsCallable<
  { endpointId: string },
  RotateWebhookSecretResponse
>(functions, "rotateWebhookSecret");

// ---------------------------------------------------------------------------
// Fehler-Normalisierung
// ---------------------------------------------------------------------------

/**
 * Mappt einen Firebase-Callable-Fehler auf einen `Error` mit angehängtem
 * `.code` (z.B. `failed-precondition`, wenn `WEBHOOKS_ENABLED` serverseitig
 * aus ist). Aufrufer können so gezielt zwischen "Feature noch nicht aktiv" und
 * echten Fehlern unterscheiden, ohne die Firebase-SDK-Fehlerform zu leaken.
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === "string" && anyErr.message.length > 0
      ? anyErr.message
      : "Request failed.";
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === "string") {
    // firebase callable errors carry codes like 'functions/failed-precondition'.
    out.code = anyErr.code.replace(/^functions\//, "");
  }
  return out;
}

// ---------------------------------------------------------------------------
// Öffentliche API
// ---------------------------------------------------------------------------

/**
 * Legt einen neuen Webhook-Endpoint für den eingeloggten User an.
 *
 * @param url Ziel-URL (https-only, serverseitig SSRF-validiert).
 * @param events Nicht-leere Liste von Event-Typen (siehe {@link WEBHOOK_EVENT_TYPES}).
 * @returns CreateWebhookEndpointResponse inkl. einmaligem Klartext-Signatursecret.
 * @throws Error (ggf. mit `.code`), wenn url/events leer sind oder die Function fehlschlägt
 *   (z.B. `failed-precondition` bei deaktiviertem Feature, `invalid-argument` bei ungültiger URL).
 */
export async function createWebhookEndpoint(
  url: string,
  events: string[]
): Promise<CreateWebhookEndpointResponse> {
  if (!url || url.trim() === "") {
    throw new Error("url darf nicht leer sein.");
  }
  if (!events || events.length === 0) {
    throw new Error("events darf nicht leer sein.");
  }
  logger.info("webhooksApi: createWebhookEndpoint called");
  try {
    const result: HttpsCallableResult<CreateWebhookEndpointResponse> =
      await createWebhookEndpointFn({ url: url.trim(), events });
    logger.info("webhooksApi: createWebhookEndpoint succeeded");
    return result.data;
  } catch (error: unknown) {
    logger.error("webhooksApi: createWebhookEndpoint failed", error);
    throw normalizeCallableError(error);
  }
}

/**
 * Listet alle Webhook-Endpoints des eingeloggten Users.
 * Gibt ausschließlich die öffentliche Ansicht zurück — kein Secret.
 *
 * @returns Array der vorhandenen Endpoints.
 * @throws Error (ggf. mit `.code`) bei Netzwerk-, Auth- oder Feature-Flag-Fehlern.
 */
export async function listWebhookEndpoints(): Promise<WebhookEndpointItem[]> {
  logger.info("webhooksApi: listWebhookEndpoints called");
  try {
    const result: HttpsCallableResult<ListWebhookEndpointsResponse> =
      await listWebhookEndpointsFn({});
    logger.info(
      `webhooksApi: listWebhookEndpoints returned ${result.data.endpoints.length} endpoint(s)`
    );
    return result.data.endpoints;
  } catch (error: unknown) {
    logger.error("webhooksApi: listWebhookEndpoints failed", error);
    throw normalizeCallableError(error);
  }
}

/**
 * Patcht url/events/active eines eigenen Webhook-Endpoints. Nur übergebene Felder
 * werden geändert.
 *
 * @param params.endpointId Die ID des zu ändernden Endpoints.
 * @param params.url Optionale neue Ziel-URL (wird erneut SSRF-validiert).
 * @param params.events Optionale neue Event-Auswahl.
 * @param params.active Optionaler neuer Active-Status.
 * @returns Der aktualisierte Endpoint (öffentliche Ansicht, kein Secret).
 * @throws Error (ggf. mit `.code`), wenn endpointId leer ist oder die Function fehlschlägt.
 */
export async function updateWebhookEndpoint(params: {
  endpointId: string;
  url?: string;
  events?: string[];
  active?: boolean;
}): Promise<WebhookEndpointItem> {
  const { endpointId, url, events, active } = params;
  if (!endpointId || endpointId.trim() === "") {
    throw new Error("endpointId darf nicht leer sein.");
  }
  logger.info(`webhooksApi: updateWebhookEndpoint called for endpoint ${endpointId}`);
  try {
    const result: HttpsCallableResult<WebhookEndpointItem> =
      await updateWebhookEndpointFn({
        endpointId,
        ...(url !== undefined ? { url: url.trim() } : {}),
        ...(events !== undefined ? { events } : {}),
        ...(active !== undefined ? { active } : {}),
      });
    logger.info(`webhooksApi: updateWebhookEndpoint succeeded for endpoint ${endpointId}`);
    return result.data;
  } catch (error: unknown) {
    logger.error(`webhooksApi: updateWebhookEndpoint failed for endpoint ${endpointId}`, error);
    throw normalizeCallableError(error);
  }
}

/**
 * Löscht einen eigenen Webhook-Endpoint unwiderruflich.
 *
 * @param endpointId Die ID des zu löschenden Endpoints.
 * @returns `{ success: true }` bei Erfolg.
 * @throws Error (ggf. mit `.code`), wenn endpointId leer ist, der Endpoint nicht
 *   gefunden wird oder kein Ownership besteht.
 */
export async function deleteWebhookEndpoint(
  endpointId: string
): Promise<DeleteWebhookEndpointResponse> {
  if (!endpointId || endpointId.trim() === "") {
    throw new Error("endpointId darf nicht leer sein.");
  }
  logger.info(`webhooksApi: deleteWebhookEndpoint called for endpoint ${endpointId}`);
  try {
    const result: HttpsCallableResult<DeleteWebhookEndpointResponse> =
      await deleteWebhookEndpointFn({ endpointId });
    logger.info(`webhooksApi: deleteWebhookEndpoint succeeded for endpoint ${endpointId}`);
    return result.data;
  } catch (error: unknown) {
    logger.error(`webhooksApi: deleteWebhookEndpoint failed for endpoint ${endpointId}`, error);
    throw normalizeCallableError(error);
  }
}

/**
 * Rotiert das Signatur-Secret eines eigenen Webhook-Endpoints. Anders als bei
 * API-Keys gibt es KEINE Grace-Period — das alte Secret ist sofort ungültig
 * (siehe Backend-Kommentar in webhookEndpointFunctions.ts).
 *
 * @param endpointId Die ID des Endpoints, dessen Secret rotiert werden soll.
 * @returns RotateWebhookSecretResponse inkl. einmaligem Klartext des NEUEN Secrets.
 * @throws Error (ggf. mit `.code`), wenn endpointId leer ist, der Endpoint nicht
 *   gefunden wird oder kein Ownership besteht.
 */
export async function rotateWebhookSecret(
  endpointId: string
): Promise<RotateWebhookSecretResponse> {
  if (!endpointId || endpointId.trim() === "") {
    throw new Error("endpointId darf nicht leer sein.");
  }
  logger.info(`webhooksApi: rotateWebhookSecret called for endpoint ${endpointId}`);
  try {
    const result: HttpsCallableResult<RotateWebhookSecretResponse> =
      await rotateWebhookSecretFn({ endpointId });
    logger.info(`webhooksApi: rotateWebhookSecret succeeded for endpoint ${endpointId}`);
    return result.data;
  } catch (error: unknown) {
    logger.error(`webhooksApi: rotateWebhookSecret failed for endpoint ${endpointId}`, error);
    throw normalizeCallableError(error);
  }
}
